import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import {
  Nav,
  Footer,
  usePageSeo,
  OFFICE_DISPLAY,
  CONTACT_EMAIL,
  CARRIER_REG,
  COMPANY_NUMBER,
} from "../layout/shared";

const SECTIONS = [
  {
    h: "1. Who we are",
    p: [
      "3 Counties Recycling (\"we\", \"us\", \"our\") is a commercial recycling business serving farms and businesses across Worcestershire, Herefordshire and Gloucestershire.",
      "We are the data controller for the personal information we hold about you. You can contact us using the details at the bottom of this page.",
      "3 Counties Recycling is a trading name of Three Counties Recycling Ltd (company number " +
        COMPANY_NUMBER +
        "). Registered office address: to be published here once verified on Companies House.",
    ],
  },
  {
    h: "2. What information we collect",
    p: [
      "We only collect the information we need to give you a quote, schedule a collection, complete the job and stay compliant with UK waste-carrier and duty-of-care rules. This typically includes:",
    ],
    list: [
      "Your name, business or farm name and contact details (phone, email).",
      "The address or postcode where the collection is taking place.",
      "Details of the material we are collecting (type, rough volume, photos you choose to send us).",
      "Records required for our waste-carrier obligations, including Waste Transfer Notes and Duty of Care paperwork.",
      "Basic technical data (IP address, browser type, pages visited) when you use our website.",
    ],
  },
  {
    h: "3. How we use your information",
    p: ["We use your information to:"],
    list: [
      "Reply to your enquiry and provide a quote.",
      "Plan, schedule and deliver collections.",
      "Issue Waste Transfer Notes, invoices and destruction records where applicable.",
      "Comply with our legal obligations under UK waste-carrier and environmental regulations.",
      "Improve our service and the way our website works.",
    ],
  },
  {
    h: "4. Lawful basis for processing",
    p: ["Under UK GDPR we rely on the following lawful bases:"],
    list: [
      "Contract — to give you a quote and carry out the collection you have asked for.",
      "Legal obligation — to keep the records required by waste-carrier and environmental law.",
      "Legitimate interests — to run, protect and improve our business in a way you would reasonably expect.",
    ],
  },
  {
    h: "5. Who we share your information with",
    p: ["We do not sell or rent your information. We only share it where we need to, with:"],
    list: [
      "The downstream UK reprocessors and recycling sites that take the material on from us.",
      "The Environment Agency and other regulators, where we are legally required to.",
      "Trusted suppliers who help us run the business (for example, accounting, IT and communications providers), under appropriate written terms.",
      "WhatsApp / Meta if you choose to contact us via WhatsApp — your message is governed by their privacy policy as well as ours.",
    ],
  },
  {
    h: "6. How long we keep it",
    p: [
      "We keep customer and job records for as long as the relationship is active, and afterwards for as long as we are legally required to. Non-hazardous waste-transfer records are retained for at least two years. Tax and accounting records are normally retained for six years from the end of the relevant financial year. Website analytics data is kept for a shorter period.",
    ],
  },
  {
    h: "7. Your rights",
    p: ["You have the right to ask us to:"],
    list: [
      "See a copy of the personal data we hold about you.",
      "Correct anything that is inaccurate.",
      "Delete your data, where we no longer need it and we are not legally required to keep it.",
      "Object to certain uses, or ask us to restrict how we use it.",
      "Receive a copy in a portable format.",
    ],
    pAfter: [
      "To exercise any of these rights, please email us at the address below. You also have the right to complain to the Information Commissioner's Office (ico.org.uk).",
    ],
  },
  {
    h: "8. Cookies and the website",
    p: [
      "Our website uses a small number of cookies and similar technologies. Essential storage is used to deliver, secure and operate the website. Optional analytics or session-recording technologies are not used before appropriate information and choice have been provided where consent is required.",
    ],
  },
  {
    h: "9. Changes to this policy",
    p: [
      "We may update this policy from time to time. The latest version will always be available at this page.",
    ],
  },
  {
    h: "10. Contact us",
    p: [
      `Questions about this policy or about your data? Email ${CONTACT_EMAIL} or call ${OFFICE_DISPLAY}.`,
    ],
  },
];

const Privacy = () => {
  usePageSeo({
    title: "Privacy Policy · Three Counties Recycling",
    description:
      "Privacy policy for Three Counties Recycling Ltd — how we handle your data when you book a collection, deliver material or use this site.",
    path: "/privacy",
  });

  return (
    <main
      data-testid="privacy-root"
      className="bg-white text-[#1a1a1a] min-h-screen"
    >
      <Nav />

      <section className="bg-[#024120] text-white py-20 md:py-28 grain overflow-hidden relative">
        <div className="max-w-4xl mx-auto px-6 md:px-10 relative">
          <p className="font-mono text-xs uppercase tracking-[0.28em] text-[#46B649]">
            / Legal
          </p>
          <h1
            data-testid="privacy-h1"
            className="font-display font-black text-4xl md:text-6xl tracking-tight mt-3 leading-[1.05]"
          >
            Privacy
            <br />
            <span className="text-[#46B649]">policy.</span>
          </h1>
          <p className="mt-6 text-white/75 text-base md:text-lg max-w-2xl leading-relaxed">
            How we handle your data when you contact us, book a collection or
            use this website.
          </p>
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
            Last updated: February 2026
          </p>
        </div>
      </section>

      <article className="max-w-4xl mx-auto px-6 md:px-10 py-20 md:py-28 space-y-12">
        {SECTIONS.map((s) => (
          <section
            key={s.h}
            data-testid={`privacy-${s.h.split(".")[0].trim()}`}
          >
            <h2 className="font-display font-black text-xl md:text-2xl tracking-tight text-[#024120]">
              {s.h}
            </h2>
            {s.p?.map((para, i) => (
              <p
                key={`${s.h}-p-${i}`}
                className="mt-4 text-[#1a1a1a] text-base leading-relaxed"
              >
                {para}
              </p>
            ))}
            {s.list && (
              <ul className="mt-4 space-y-2.5">
                {s.list.map((li) => (
                  <li
                    key={li}
                    className="flex items-start gap-3 text-[#1a1a1a] text-base leading-relaxed"
                  >
                    <Check
                      className="w-4 h-4 mt-1.5 text-[#46B649] shrink-0"
                      strokeWidth={2}
                    />
                    <span>{li}</span>
                  </li>
                ))}
              </ul>
            )}
            {s.pAfter?.map((para, i) => (
              <p
                key={`${s.h}-pa-${i}`}
                className="mt-4 text-[#1a1a1a] text-base leading-relaxed"
              >
                {para}
              </p>
            ))}
          </section>
        ))}

        <section
          data-testid="privacy-carrier"
          className="border-t border-[#024120]/15 pt-8 text-sm text-[#4A4A4A] leading-relaxed"
        >
          Three Counties Recycling Ltd, registered in England and Wales,
          company number {COMPANY_NUMBER}. Registered upper-tier waste
          carrier, broker and dealer: {CARRIER_REG}. Waste carrier
          registration is renewed every three years. Registration does not by
          itself authorise storage or treatment at a site.
        </section>

        <div className="pt-6">
          <Link
            to="/"
            data-testid="privacy-back-bottom"
            className="inline-flex items-center gap-2 bg-[#024120] hover:bg-[#012b14] text-white font-bold px-6 py-3.5 transition-all hover:-translate-y-1"
          >
            <ArrowRight className="w-4 h-4 rotate-180" /> Back to the site
          </Link>
        </div>
      </article>

      <Footer />
    </main>
  );
};

export default Privacy;
